/**
 * Static historical data bundled with the app.
 *
 * These are the original sources from before fund data moved into Firestore.
 * They serve two purposes:
 *  1. Seeding Firestore from the Fund Admin panel (one-time migration).
 *  2. Graceful fallback so the Dashboard / Letters views always show history
 *     even before the Firestore collections have been seeded.
 */

import Papa from 'papaparse';
import { toIsoDate } from '../utils/format';

/** Historical letters served from /public/letters. */
export const LEGACY_LETTERS = [
  { title: 'Q3 2024 Update', date: '2024-10-01', url: '/letters/3Q24-Update.pdf', type: 'Quarterly' },
  { title: 'Q2 2024 Update', date: '2024-08-02', url: '/letters/2Q24-Update.pdf', type: 'Quarterly' },
  { title: 'Q1 2024 Update', date: '2024-06-06', url: '/letters/1Q24-Update.pdf', type: 'Quarterly' },
  { title: 'Fiscal Year 2023 Annual Report', date: '2024-03-05', url: '/letters/FY2023-Update.pdf', type: 'Annual' },
  { title: 'Q2 2023 Update', date: '2023-06-30', url: '/letters/2Q23-Update.pdf', type: 'Quarterly' },
  { title: 'Q1 2023 Update', date: '2023-04-26', url: '/letters/1Q23-Update.pdf', type: 'Quarterly' },
  { title: 'Q4 2022 Update', date: '2023-01-13', url: '/letters/4Q22-Update.pdf', type: 'Quarterly' },
  { title: 'Q3 2022 Update', date: '2022-11-11', url: '/letters/3Q22-Update.pdf', type: 'Quarterly' },
  { title: 'Q2 2022 Update', date: '2022-08-04', url: '/letters/2Q22-Update.pdf', type: 'Quarterly' },
  { title: 'Q1 2022 Update', date: '2022-04-01', url: '/letters/1Q22-Update.pdf', type: 'Quarterly' },
  { title: 'Q4 2021 & Q1 2022 Update', date: '2022-03-13', url: '/letters/4Q21-1Q22-Update.pdf', type: 'Quarterly' },
  { title: 'Q2 & Q3 2021 Update', date: '2021-10-04', url: '/letters/2Q21-3Q21-Update.pdf', type: 'Quarterly' },
  { title: 'Q1 2021 Update', date: '2021-04-05', url: '/letters/1Q21-Update.pdf', type: 'Quarterly' },
  { title: 'Fiscal Year 2020 Annual Report', date: '2021-01-15', url: '/letters/FY2020-Update.pdf', type: 'Annual' },
  { title: 'Q3 2020 Update', date: '2020-10-09', url: '/letters/3Q20-Update.pdf', type: 'Quarterly' },
  { title: 'Q2 2020 Update', date: '2020-07-02', url: '/letters/2Q20-Update.pdf', type: 'Quarterly' },
  { title: 'Fiscal Year 2019 Annual Report', date: '2020-01-06', url: '/letters/FY19-Annual-Report.pdf', type: 'Annual' },
];

/** Path to the bundled performance CSV in /public. */
export const STATIC_PERFORMANCE_CSV = '/monthly_portval9.csv';

/**
 * Load and parse the bundled performance CSV into the same shape the
 * Firestore `performance` collection uses: { date, value, contributedCapital }.
 * Returns [] if the file is missing or empty.
 */
export const loadStaticPerformance = async () => {
  const res = await fetch(STATIC_PERFORMANCE_CSV);
  if (!res.ok) throw new Error(`Could not load bundled performance CSV (${res.status})`);
  const text = await res.text();
  const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
  return parsed.data
    .filter((row) => row.date && row.Ragnar && row['Contributed Capital'])
    .map((row) => ({
      date: toIsoDate(row.date),
      value: Number(row.Ragnar),
      contributedCapital: Number(row['Contributed Capital']),
    }));
};
